import { useState } from "react";
import Modal from "react-responsive-modal";
import { useAuth, useSavedPlaces } from "common/hooks";
import type { Venue } from "common/types";
import CreateList from "@/components/SavedPlaces/CreateList";
import AddRemoveVenueToList from "@/components/SavedPlaces/AddRemoveVenueToList";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { useScrollLock } from "@/hooks/useScrollLock";

/**
 * Save-to-list for venue cards on pages without the search list: signed-out visitors get the
 * sign-in modal, signed-in ones pick or create a list.
 *
 * @returns The cards' save props and the modal element to render once on the page.
 */
export function useSaveVenueFlow() {
  const { user, isLoggedIn } = useAuth();
  const { showModal } = useAuthModals();
  const { data, refetch } = useSavedPlaces();
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<"list" | "create">("list");
  const [venueToSave, setVenueToSave] = useState<Venue | null>(null);
  const [listNameCreated, setListNameCreated] = useState("");
  useScrollLock(isOpen);

  const reset = () => {
    setIsOpen(false);
    setVenueToSave(null);
    setListNameCreated("");
  };

  const onSaveVenue = async (venue: Venue) => {
    if (!user) {
      showModal(AuthModalScreen.EnterEmail);
      return;
    }
    setVenueToSave(venue);
    const { data: refreshed } = await refetch();
    setMode(refreshed && refreshed.saved_places?.length > 0 ? "list" : "create");
    setIsOpen(true);
  };

  const handleClose = async () => {
    setIsOpen(false);
    if (mode !== "create") {
      reset();
      return;
    }
    // Back to the picker so the venue can go into the new list; with no lists, a cancel just closes.
    const { data: refreshed } = await refetch();
    if (refreshed && refreshed.saved_places?.length > 0) {
      setMode("list");
      setIsOpen(true);
    } else {
      reset();
    }
  };

  const handleCreateNew = () => {
    setMode("create");
    setIsOpen(true);
  };

  const businessId = venueToSave?.business?.id ?? 0;

  const saveModal = isOpen && (
    <Modal
      focusTrapped
      center
      blockScroll={true}
      animationDuration={0}
      open={isOpen}
      onClose={handleClose}
      ariaLabelledby="modal-title"
      classNames={{
        modal: "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
        closeButton: "hidden",
      }}
    >
      {mode === "create" && (
        <CreateList
          onClose={handleClose}
          businessId={businessId}
          venueDetails={venueToSave}
          onCreateList={setListNameCreated}
        />
      )}
      {mode === "list" && (
        <AddRemoveVenueToList
          onClickCreateNew={handleCreateNew}
          onClose={handleClose}
          businessId={businessId}
          venueDetails={venueToSave}
          listNameCreated={listNameCreated}
        />
      )}
    </Modal>
  );

  return {
    savedPlaces: data?.saved_places || [],
    onSaveVenue,
    isLoggedIn,
    onShowAuthModal: () => showModal(AuthModalScreen.EnterEmail),
    saveModal,
  };
}
